import { DerivedAvailabilityStatus, DerivedAvailabilityStatusInternal } from '@commerce-product-webcomponents/sdk';
import { useTranslation } from '@nextgen-web-framework/all';
import { DateFormats, DateOrTime, formatDateTime } from '@nextgen-web-framework/core';
import { FC } from 'react';
import styles from './StockStatusComponent.module.scss';
import {
  DerivedAvailabilityStatusComponentGroupKey,
  DerivedAvailabilityStatusComponentType,
  DerivedAvailabilityStatusLabelEnums,
} from './types/enums';
import type { StockStatusComponentPropsTypes, StockStatusTextPropsTypes } from './types/propsTypes';

const StatusText = ({
  derivedAvailabilityStatus,
  className,
  subtext,
  dateSubText,
  firstAvailableFutureDate,
  showPrefix = true,
}: StockStatusTextPropsTypes): JSX.Element => {
  const t = useTranslation();
  const configs = ConfigService.getConfigs();
  const formattedDate = firstAvailableFutureDate
    ? formatDateTime(
        firstAvailableFutureDate,
        { date: DateFormats.MEDIUM_DATE },
        false,
        configs.NEXT_PUBLIC_TIMEZONE as string,
        undefined,
        DateOrTime.DATE_ONLY,
      )
    : '';

  const stockStatusKey = 'StockStatus';
  const prefixText = showPrefix ? t('product.StockStatus.backOrderAvailableOn') : '';
  return (
    <>
      <div className={`${className}`}>
        <div className={styles['stock-status-title']} data-testid="stock-status-text">
          {t(
            `product.${stockStatusKey}.${
              DerivedAvailabilityStatusLabelEnums[
                derivedAvailabilityStatus as keyof typeof DerivedAvailabilityStatusLabelEnums
              ]
            }`,
          )}
        </div>
        {!!subtext && (
          <div className={styles['stock-status-subtext']} data-testid="stock-status-subtext">
            {formattedDate ? `${prefixText} ${formattedDate}` : t(`product.${stockStatusKey}.${subtext}`)}
          </div>
        )}
        {!subtext && dateSubText && (
          <div className={styles['stock-status-date-subtext']} data-testid="stock-status-date-subtext">
            {formattedDate ? `${prefixText} ${formattedDate}` : dateSubText}
          </div>
        )}
      </div>
    </>
  );
};

const StockStatusComponent: FC<StockStatusComponentPropsTypes> = ({
  derivedAvailabilityStatus,
  dateSubText,
  showSubText = true,
  stockStatusType,
  firstAvailableFutureDate,
  showPrefix = true,
}: StockStatusComponentPropsTypes): JSX.Element => {
  const getClassName = () => {
    switch (stockStatusType) {
      case DerivedAvailabilityStatusComponentType.ONE_LINER:
        return 'stock-status-one-liner';
      case DerivedAvailabilityStatusComponentType.STOCK_STATUS_CART_ENTRY:
        return 'stock-status-product-cart-entry';
      case DerivedAvailabilityStatusComponentType.STOCK_STATUS_CART_ENTRY_MOBILE:
        return 'stock-status-product-cart-entry-mobile';
      case DerivedAvailabilityStatusComponentType.STOCK_STATUS_CARD_COMPONENT:
        return 'stock-status-product-card-component';
      case DerivedAvailabilityStatusComponentType.STOCK_STATUS_HORIZONTAL_CARD_COMPONENT:
        return 'stock-status-product-horizontal-card-component';
      case DerivedAvailabilityStatusComponentType.STOCK_STATUS_BUTTON:
        return 'stock-status-product-button';
      case DerivedAvailabilityStatusComponentType.STOCK_STATUS_SEARCH_CARD:
        return 'stock-status-product-search-card';
      case DerivedAvailabilityStatusComponentType.STOCK_STATUS_VARIANT:
        return 'stock-status-variant';
      case DerivedAvailabilityStatusComponentType.STOCK_STATUS_ABOCHECKBOX:
        return 'stock-status-abo-checkbox';
      case DerivedAvailabilityStatusComponentType.STOCK_STATUS_SEARCH_RESULT_CARD:
        return 'stock-status-search-result-card';
      case DerivedAvailabilityStatusComponentType.CHECKOUT_CARD:
        return 'stock-status-checkout-card';
      default:
        return 'stock-status';
    }
  };
  let stockStatusKey = '';
  for (const propKey in DerivedAvailabilityStatusInternal) {
    if (
      DerivedAvailabilityStatusInternal[propKey as keyof typeof DerivedAvailabilityStatusInternal] ===
      derivedAvailabilityStatus
    ) {
      stockStatusKey = propKey;
    }
  }
  switch (derivedAvailabilityStatus) {
    case DerivedAvailabilityStatusInternal.OutOfStock:
    case DerivedAvailabilityStatusInternal.Cancelled:
    case DerivedAvailabilityStatusInternal.Nla:
      return (
        <StatusText
          derivedAvailabilityStatus={stockStatusKey as string}
          className={`${styles[getClassName()]} ${styles['error-stock-status']}`}
          showPrefix={showPrefix}
        />
      );
    case DerivedAvailabilityStatusInternal.LowStock:
      return (
        <StatusText
          derivedAvailabilityStatus={stockStatusKey as string}
          subtext={showSubText ? DerivedAvailabilityStatusLabelEnums.LowStockSubtext : ''}
          className={`${styles[getClassName()]} ${styles['alert-stock-status']}`}
          showPrefix={showPrefix}
        />
      );
    case DerivedAvailabilityStatusInternal.Nya:
      return (
        <StatusText
          derivedAvailabilityStatus={stockStatusKey as string}
          subtext={showSubText ? DerivedAvailabilityStatusLabelEnums.BackOrderSubtext : ''}
          className={`${styles[getClassName()]} ${styles['nya-stock-status']}`}
          dateSubText={dateSubText}
          firstAvailableFutureDate={firstAvailableFutureDate}
          showPrefix={showPrefix}
        />
      );

    case DerivedAvailabilityStatusInternal.Bo:
    case DerivedAvailabilityStatusInternal.Pbo:
      return (
        <StatusText
          derivedAvailabilityStatus={stockStatusKey as string}
          subtext={showSubText ? DerivedAvailabilityStatusLabelEnums.BackOrderSubtext : ''}
          className={`${styles[getClassName()]} ${styles['bo-stock-status']} ${styles['bo-date-subtext']}`}
          dateSubText={dateSubText}
          firstAvailableFutureDate={firstAvailableFutureDate}
          showPrefix={showPrefix}
        />
      );

    default:
      return <></>;
  }
};

export default StockStatusComponent;
export {
  DerivedAvailabilityStatus,
  DerivedAvailabilityStatusComponentGroupKey as StockStatusComponentGroupKey,
  DerivedAvailabilityStatusComponentType as StockStatusComponentType,
};
export type { StockStatusComponentPropsTypes };
